import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiClose, mdiFileDocumentOutline } from "@mdi/js";

import { CriteriaEntityGetResponse } from "src/entities/CriteriaEntity";
import CriteriaService, { criteriaQueryKeys } from "src/services/CriteriaService";
import { criteriaGroupQueryKeys } from "src/services/CriteriaGroupService";
import { criteriaClassificationQueryKeys } from "src/services/CriteriaClassificationService";
import { criteriaManifestationQueryKeys } from "src/services/CriteriaManifestationService";
import { criteriaRiskQueryKeys } from "src/services/CriteriaRiskService";
import { criteriaRecomendationQueryKeys } from "src/services/CriteriaRecomendationService";
import { criteriaDomainQueryKeys } from "src/services/CriteriaDomainService";
import { useToast } from "src/context/ToastContext";

import Layout from "src/components/Layout";
import FormActionButtons from "src/components/FormActionButtons";
import CriteriaPreview from "./CriteriaPreview";
import StepTitle from "./StepTitle";
import StepGroups from "./StepGroups";
import StepClassifications from "./StepClassifications";
import StepManifestations from "./StepManifestations";
import StepRisks from "./StepRisks";
import StepRecomendations from "./StepRecomendations";
import StepDomains from "./StepDomains";
import { clearCriteriaDrafts } from "./draftStorage";

const criteriaService = new CriteriaService();

type FormMode = "view" | "edit" | "create";

type FormStage = 
	"title" |
	"groups" | 
	"classifications" | 
	"manifestations" | 
	"risks" | 
	"recomendations" | 
	"domains";

export default function CriterioFormulario() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [mode, setMode] = useState<FormMode>(id ? "view" : "create");
	const [formStage, setFormStage] = useState<FormStage>("title");
	const [showCancelEditModal, setShowCancelEditModal] = useState(false);

	const criteriaId = Number(id);

	const { data, isLoading, isFetching, isRefetching } = useQuery<CriteriaEntityGetResponse>(
		criteriaQueryKeys.get(id!),
		() => criteriaService.get(criteriaId).then(response => response.data),
		{ enabled: Boolean(id), refetchOnWindowFocus: false }
	);

	function handleCancel() {
		if (id) {
			setMode("view");
			setFormStage("title");
		} else {
			navigate("/configuracoesCriterios");
		}
	}

	function handleEditStart() {
		setMode("edit");
		setFormStage("title");
	}

	function handleEditComplete() {
		queryClient.invalidateQueries(criteriaGroupQueryKeys.groups(id!));
		queryClient.invalidateQueries(criteriaClassificationQueryKeys.classifications(id!));
		setMode("view");
		setFormStage("title");
		handleToast("Critério", "Critério atualizado com sucesso!", 5000);
	}

	function clearCurrentCriteriaDrafts() {
		if (!criteriaId) {
			return;
		}

		clearCriteriaDrafts(criteriaId);
	}

	function invalidateCriteriaQueries() {
		if (!id) {
			return;
		}

		queryClient.invalidateQueries(criteriaQueryKeys.get(id));
		queryClient.invalidateQueries(criteriaGroupQueryKeys.groups(id));
		queryClient.invalidateQueries(criteriaClassificationQueryKeys.classifications(id));
		queryClient.invalidateQueries(criteriaManifestationQueryKeys.manifestations(id));
		queryClient.invalidateQueries(criteriaRiskQueryKeys.risks(id));
		queryClient.invalidateQueries(criteriaRecomendationQueryKeys.recomendations(id));
		queryClient.invalidateQueries(criteriaDomainQueryKeys.domains(id));
	}

	function handleCancelEditConfirm() {
		clearCurrentCriteriaDrafts();
		invalidateCriteriaQueries();
		setShowCancelEditModal(false);
		setMode("view");
		setFormStage("title");
	}

	const header = (
		<h5 className="mt-4 mb-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3 fw-light">
			<Link to="/configuracoesCriterios" className="d-flex align-items-center text-decoration-none">
				<Icon path={mdiChevronLeft} size={1} className="me-1" />
				<Icon path={mdiFileDocumentOutline} size={1} className="me-1" />
				Formulário de Critério
			</Link>
			{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
			{mode === "view" && (
				<FormActionButtons
					formStatus={false}
					saving={false}
					onEdit={handleEditStart}
					onCancel={handleCancel}
					className="ms-sm-auto"
				/>
			)}
			{mode === "edit" && (
				<Button
					variant="light"
					className="rounded-3 shadow-sm ms-sm-auto d-flex align-items-center gap-2"
					type="button"
					onClick={() => setShowCancelEditModal(true)}
				>
					<Icon path={mdiClose} size={0.8} />
					Encerrar Alteração
				</Button>
			)}
		</h5>
	);

	return (
		<Layout>
			{header}

			{mode === "create" && (
				<StepTitle
					defaultValues={{ title: "", text: "" }}
					backLabel="Cancelar"
					nextLabel="Salvar"
					onBack={handleCancel}
					onComplete={(_savedData, newId) => {
						setMode("view");
						navigate("/criterioFormulario/" + newId);
					}}
				/>
			)}

			{mode === "edit" && formStage === "title" && (
				<StepTitle
					criteriaId={criteriaId}
					defaultValues={{ title: data?.title ?? "", text: data?.text ?? "" }}
					backLabel="Cancelar"
					nextLabel="Continuar"
					isLoading={isLoading}
					onBack={handleCancel}
					onComplete={(savedData) => {
						queryClient.setQueryData(criteriaQueryKeys.get(id!), (old: any) => ({ ...old, ...savedData }));
						setFormStage("groups");
					}}
				/>
			)}

			{mode === "edit" && formStage === "groups" && (
				<StepGroups
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Continuar"
					onBack={() => setFormStage("title")}
					onComplete={() => setFormStage("classifications")}
				/>
			)}

			{mode === "edit" && formStage === "classifications" && (
				<StepClassifications
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Continuar"
					onBack={() => setFormStage("groups")}
					onComplete={() => setFormStage("manifestations")}
				/>
			)}

			{mode === "edit" && formStage === "manifestations" && (
				<StepManifestations
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Continuar"
					onBack={() => setFormStage("classifications")}
					onComplete={() => setFormStage("risks")}
				/>
			)}

			{mode === "edit" && formStage === "risks" && (
				<StepRisks
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Continuar"
					onBack={() => setFormStage("manifestations")}
					onComplete={() => setFormStage("recomendations")}
				/>
			)}

			{mode === "edit" && formStage === "recomendations" && (
				<StepRecomendations
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Continuar"
					onBack={() => setFormStage("risks")}
					onComplete={() => setFormStage("domains")}
				/>
			)}

			{mode === "edit" && formStage === "domains" && (
				<StepDomains
					criteriaId={criteriaId}
					backLabel="Voltar"
					nextLabel="Concluir"
					onBack={() => setFormStage("recomendations")}
					onComplete={handleEditComplete}
				/>
			)}

			{mode === "view" && <CriteriaPreview criteriaId={criteriaId} />}

			<Modal show={showCancelEditModal} onHide={() => setShowCancelEditModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Encerrar Alteração</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Deseja realmente encerrar a edição desta etapa? As alterações não salvas serão descartadas.
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowCancelEditModal(false)}>
						Voltar
					</Button>
					<Button variant="danger" onClick={handleCancelEditConfirm}>
						Encerrar Alteração
					</Button>
				</Modal.Footer>
			</Modal>
		</Layout>
	);
}
